import {
  SandpackCodeEditor,
  SandpackPreview,
  SandpackConsole,
  SandpackFiles,
} from "@codesandbox/sandpack-react";
import { amethyst } from "@codesandbox/sandpack-themes";
import GetFiles from "../mySandpack/GetFiles";
import { CircleNotch } from "@phosphor-icons/react";
import MySandpackProvider from "../mySandpack/MySandpackProvider";
import { useContext } from "react";
import {
  ActivePracticeContext,
  PracticesContext,
} from "../../contexts/contextExporter";

import PracticeTitleContent from "../../components/PracticeTitleContent";
import AiContent from "./AiAnswer";
import { RevealAnswerButton, RevealedSandpackAnswer } from "./RevealAnswer";
import { IPracticeHistoryItem } from "../../types/generalTypes";
import PracticeHistoryNav from "./PracticeHistoryNav";
import { IPracticeReact } from "../../types/practiceTypes";
import TripleLayout from "../mySandpack/TripleLayout";

export default function CodePracticeSandpack({
  currentAiContent,
  setAiReply,
  setUserAnswer,
  history,
  isLive,
}: {
  currentAiContent: string | null;
  setAiReply: React.Dispatch<React.SetStateAction<string | null>>;
  setUserAnswer: React.Dispatch<React.SetStateAction<string | null>>;
  history: IPracticeHistoryItem[];
  isLive: boolean;
}) {
  const {
    practice,
    sendAnswer,
    actionStatus,
    setActionStatus,
    isAnswerRevealed,
  } = useContext(ActivePracticeContext);
  const { nextPractice } = useContext(PracticesContext);

  const sandpackPractice = practice as IPracticeReact;


  const isPastItem = actionStatus.historyIndex > -1;
  const currentHistoryItem = history[actionStatus.historyIndex];

  const files =
    isPastItem &&
      currentHistoryItem?.user_answer &&
      JSON.parse(currentHistoryItem?.user_answer)
      ? JSON.parse(currentHistoryItem?.user_answer)
      : sandpackPractice.description.preFiles;

  const wantedFiles =
    sandpackPractice.description.spOptions?.wantedFiles || "/index.js";

  const tryAgain = () => {
    setActionStatus((prev) => ({ ...prev, ai: "IDLE", historyIndex: -1 }));
    setAiReply(null);
  };

  const onSubmit = (files: SandpackFiles) => {
    const userAnswerObj: Record<string, string> = {};
    wantedFiles.split("|").forEach((element: string) => {
      userAnswerObj[element] =
        typeof files[element] === "string"
          ? files[element]
          : (files[element] as { code: string }).code;
    });

    setUserAnswer(JSON.stringify(userAnswerObj));
    sendAnswer({ answer: JSON.stringify(userAnswerObj) });
  };

  return (
    <MySandpackProvider
      template={sandpackPractice.description.spOptions?.template || "react"}
      theme={amethyst}
      className="mySpProvider"
      options={{
        autoReload: false,
        autorun: true,
        ...sandpackPractice.description.spOptions,
      }}
      id={String(sandpackPractice.id)}
      customSetup={sandpackPractice.description.spOptions?.customSetup || {}}
      files={files}
    >
      <TripleLayout
        cx="h-full"
        header={
          <PracticeHistoryNav
            key={practice!.id}
            current={actionStatus.historyIndex}
            total={history.length}
            setCurrent={(newHistoryIndex) =>
              setActionStatus((prev) => ({
                ...prev,
                historyIndex: newHistoryIndex,
              }))
            }
          />
        }
        left={
          <div className="text-base leading-normal h-full overflow-auto relative group">
            <div className="py-6 px-5 mb-2">
              <div className="practiceTitle font-medium p-6 text-white">
                <PracticeTitleContent title={practice!.description.title} />
              </div>

              {isAnswerRevealed && <RevealedSandpackAnswer />}

              <AiContent
                isFocused={isPastItem || actionStatus.ai !== "IDLE"}
                aiReply={currentAiContent}
                setAiReply={setAiReply}
              />
            </div>
            <span className="text-sm text-white absolute bottom-3 right-3 transition-opacity opacity-30 group-hover:opacity-75">
              #{practice!.id}
            </span>
          </div>
        }
        right={[
          <SandpackCodeEditor
            className="h-full"
            wrapContent
            showTabs={true}
            readOnly={isPastItem || actionStatus.ai !== "IDLE"}
            key={"sp_pr_" + sandpackPractice.id + isPastItem}
          />,
          <div className="flex h-full">
            <SandpackPreview
              className={`${sandpackPractice.description.spOptions?.hasPreview !== false
                ? "flex-[2]"
                : "h-0 w-0 overflow-hidden"
                }`}
              showNavigator={
                !!sandpackPractice.description.spOptions?.showNavigator
              }
              showOpenInCodeSandbox={false}
            />
            {sandpackPractice.description.spOptions?.hasConsole && (
              <SandpackConsole
                className="h-full flex-1"
                resetOnPreviewRestart={true}
              />
            )}
          </div>,
        ]}
        footer={
          <div className="flex gap-2">
            {isPastItem && !actionStatus.canGoNext && !isAnswerRevealed && (
              <button className="practice-button" onClick={tryAgain}>
                Tekrar dene
              </button>
            )}

            {isPastItem && actionStatus.canGoNext && (
              <button
                className="practice-button bg-myGreen/80 border border-myGreen text-black/80 ml-auto hover:bg-myGreen"
                onClick={nextPractice}
              >
                Sonraki Soru
              </button>
            )}

            {!isPastItem && actionStatus.ai === "LOADING" && (
              <button className="practice-button opacity-60 cursor-wait" disabled>
                <CircleNotch size={16} className="animate-spin inline mr-1.5" />
                Değerlendiriliyor...
              </button>
            )}
            {!isPastItem && actionStatus.ai === "IDLE" && !isAnswerRevealed && <GetFiles handleSend={onSubmit} />}

            <RevealAnswerButton tryAgain={tryAgain} isLive={isLive} />
          </div>
        }
      />
    </MySandpackProvider>
  );
}
